import { createContext, useContext, useState, useEffect, ReactNode } from "react";

type LayoutMode = "auto" | "desktop" | "mobile";

interface LayoutContextType {
  layoutMode: LayoutMode;
  setLayoutMode: (mode: LayoutMode) => void;
  isMobileLayout: boolean;
}

const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

const AUTO_LAYOUT_BREAKPOINT = 768;
const LAYOUT_STORAGE_KEY = "decorum-layout-mode";

const isLayoutMode = (value: string | null): value is LayoutMode =>
  value === "auto" || value === "desktop" || value === "mobile";

const getAutoIsMobile = () =>
  typeof window !== "undefined"
    ? window.matchMedia(`(max-width: ${AUTO_LAYOUT_BREAKPOINT - 1}px)`).matches
    : false;

export function LayoutProvider({ children }: { children: ReactNode }) {
  const [layoutMode, setLayoutMode] = useState<LayoutMode>(() => {
    if (typeof window === "undefined") return "auto";
    const saved = localStorage.getItem(LAYOUT_STORAGE_KEY);
    return isLayoutMode(saved) ? saved : "auto";
  });

  const [autoIsMobile, setAutoIsMobile] = useState(getAutoIsMobile);

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${AUTO_LAYOUT_BREAKPOINT - 1}px)`);
    const syncLayout = () => setAutoIsMobile(mql.matches);

    syncLayout();
    if (mql.addEventListener) {
      mql.addEventListener("change", syncLayout);
    } else {
      mql.addListener(syncLayout);
    }
    window.addEventListener("resize", syncLayout);

    return () => {
      if (mql.removeEventListener) {
        mql.removeEventListener("change", syncLayout);
      } else {
        mql.removeListener(syncLayout);
      }
      window.removeEventListener("resize", syncLayout);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem(LAYOUT_STORAGE_KEY, layoutMode);
  }, [layoutMode]);

  const effectiveLayoutMode: Exclude<LayoutMode, "auto"> =
    layoutMode === "auto" ? (autoIsMobile ? "mobile" : "desktop") : layoutMode;

  const isMobileLayout = effectiveLayoutMode === "mobile";

  // Apply a CSS class to the root for global responsive overrides
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove("force-mobile", "force-desktop");
    root.classList.add(effectiveLayoutMode === "mobile" ? "force-mobile" : "force-desktop");
  }, [effectiveLayoutMode]);

  return (
    <LayoutContext.Provider value={{ layoutMode, setLayoutMode, isMobileLayout }}>
      {children}
    </LayoutContext.Provider>
  );
}

export function useLayout() {
  const context = useContext(LayoutContext);
  if (!context) throw new Error("useLayout must be used within LayoutProvider");
  return context;
}
